import React, { useCallback, useEffect, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import { CircularProgress, Box } from '@mui/material';

import ControlsClient from '../components/ControlsClient/ControlsClient';
import LocalVideoPreview from '../components/LocalVideoPreview/LocalVideoPreview';
import ReconnectingNotification from '../components/ReconnectingNotification/ReconnectingNotification';
import Room from '../components/Room/Room';
import useRoomState from '../hooks/useRoomState/useRoomState';
import useVideoViewToggle from '../hooks/useVideoViewToggle/useVideoViewToggle';
import useRemoteDatatrack from '../hooks/useDatatrackParticipant/useRemoteDatatrack';
import ClientHeader from '../components/ClientHeader/ClientHeader';
import useValidSession from '../hooks/useValidSession/useValidSession';
import useAppContext from '../hooks/useAppContext/useAppContext';
import useClientCommands from '../hooks/useClientCommands/useClientCommands';
import ClientMediaPlayer from '../components/ClientMediaPlayer/ClientMediaPlayer';
import { config } from '../components/PhaserApp/components/application/config/TherapistSceneConfig';
import BackendService from '../services/BackendService';
import { ShowMessageCommandTypes } from '../commands/practitioner/enums/ShowMessageCommandTypes';
import { ModalAidMessageContentChild } from '../components/ShowAidModal/ShowAidModalChild';
import { ModalUI } from '../components/UI/Modal/Modal';
import { ModalAidMessageContentAdult } from '../components/ShowAidModal/ShowAidModalAdult';
import { Theme } from '../components/PhaserApp/enums/Themes';
import useClientMediaState from '../hooks/useClientMediaState/useClientMediaState';
import theme from '../theme';
import MediaProvider from '../components/MediaProvider';
import PhaserActionButtonGroup from '../components/UI/PhaserActionButton/PhaserActionButtonGroup';
import TaskScene from '../components/PhaserApp/components/application/scene/TaskScene';
import { useMediaQuery } from '@mui/material';

const Container = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  width: '100vw',
  height: '100vh' /* This is needed to overcome a Chrome bug. */,
});

const Main = styled('main')({
  overflow: 'hidden',
  width: '100vw',
  height: '100vh' /* This is needed to overcome a Chrome bug. */,
  float: 'left',
  display: 'flex',
  justifyContent: 'center',
  '&.child-theme': {
    backgroundImage: 'url(/assets/phaser/images/child_bg_emptylandscape.png)',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundColor: 'transparent',
  },
});

const Videoframe = styled('div')({
  height: '100%',
  overflow: 'hidden',
  width: '75%',
  margin: '0 auto',
});

const spinnerStyle = {
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
  alignItems: 'center',
  '& .MuiCircularProgress-root ': {
    color: theme.palette?.myPrimary?.lightBlue100,
  },
};

interface IVideoViewProps {
  isEnabled: boolean;
  isConnected: boolean;
  gameIsLoading: boolean;
}

function ToggleVideoView(props: IVideoViewProps) {
  const { hasDevice } = useAppContext();
  return (
    <>
      <Video isEnabled={props.isEnabled} />
      {hasDevice ? null : !props.gameIsLoading ? (
        <div id="client-task-container" style={{ width: '100%', height: '100%', zIndex: 2 }}></div>
      ) : (
        <Box sx={spinnerStyle}>
          <CircularProgress color="primary" />
        </Box>
      )}
      <ControlsClient />
      <ReconnectingNotification />
    </>
  );
}

const Video: React.FC<{ isEnabled: boolean }> = ({ isEnabled }) => {
  const roomState = useRoomState();

  return (
    <div style={{ display: isEnabled ? 'block' : 'none', width: '100vw', height: '100vh' }}>
      <div style={{ position: 'absolute', zIndex: 2, width: '250px', bottom: '80px' }}>
        <LocalVideoPreview />
      </div>
      <div style={{ position: 'absolute', zIndex: 1, width: '100%', bottom: '5px' }}>
        <Videoframe>{roomState === 'disconnected' ? <LocalVideoPreview /> : <Room />}</Videoframe>
      </div>
    </div>
  );
};

function setWindowSize() {
  const width = 100 + 'vw';
  const height = 100 + 'vh';
  return [width, height];
}

export default function ClientPage(props: any) {
  useValidSession();
  const { hasDevice } = useAppContext();
  const { isEnabled, setVideoViewState } = useVideoViewToggle(false);
  const remoteDatatrack = useRemoteDatatrack();
  const [width, height] = setWindowSize();
  const [clientInstructionsOpened, setClientInstructionsOpened] = useState(true);
  const { room, isConnectedToCall } = useClientCommands();
  const [aidTheme, setAidTheme] = useState(0);
  const [openAidMessage, setOpenAidMessage] = useState(false);
  const [showMessageAidScore, setShowMessageAidScore] = useState(ShowMessageCommandTypes.Hide);
  const [gameIsLoading, setGameIsLoading] = useState<boolean>(true);
  const [isVisualActive, setVideoActive] = useState<boolean>(false);
  const [isAudioActive, setAudioActive] = useState<boolean>(false);
  const [isCalculationActive, setCalculationActive] = useState<boolean>(false);
  const [isStroopActive, setStroopActive] = useState<boolean>(false);
  const [isShapeActive, setShapeActive] = useState<boolean>(false);
  const isTablet = useMediaQuery(
    '(pointer: coarse) and (max-width: 1400px), (pointer: coarse) and (orientation: landscape)'
  );
  const handleDisconnectUser = useCallback(() => {
    if (room && typeof room.disconnect === 'function') {
      room.disconnect();
    }
  }, [room]);
  const [isTaskStarted, setIsStarted] = useState(false);
  const phaserRef = useRef<Phaser.Game | null>(null);
  const { clientMediaURI } = useClientMediaState();
  const [isVideoConnetctionOn, setIsVideoConnetctionOn] = useState<boolean>(false);

  useEffect(() => {
    // Recommended by Twilio to disconnect from rooms on unloading the UI.
    window.addEventListener('beforeunload', handleDisconnectUser);

    const handleParticipantDisconnected = () => {
      if (room && typeof room.disconnect === 'function') {
        room.disconnect();
      }
    };

    if (room) {
      room.on('participantDisconnected', handleParticipantDisconnected);
    }

    return () => {
      window.removeEventListener('beforeunload', handleDisconnectUser);
      if (room) {
        room.off('participantDisconnected', handleParticipantDisconnected);
      }
    };
  }, [room, handleDisconnectUser]);

  useEffect(() => {
    if (remoteDatatrack) {
      remoteDatatrack.on('subscribed', track => {
        track.on('message', (msg: any) => {
          if (msg) {
            const { type, data } = JSON.parse(msg);
            if ((type as string) === 'VideoFrame Command') {
              setVideoViewState(data);
            }
          }
        });
      });
    }
    return () => {
      if (remoteDatatrack) {
        remoteDatatrack.off('subscribed', track => {
          track.off('message', () => {});
        });
      }
    };
  }, [remoteDatatrack, setVideoViewState]);

  const { sessionId, treatmentId } = useAppContext();

  useEffect(() => {
    const onLoad = () => setGameIsLoading(false);

    if (document.readyState === 'complete') {
      onLoad();
    } else {
      window.addEventListener('load', onLoad);
      return () => window.removeEventListener('load', onLoad);
    }
  }, []);

  useEffect(() => {
    if (!gameIsLoading) {
      phaserRef.current = new Phaser.Game(config);
    }
    return () => {
      phaserRef.current?.destroy(true);
      phaserRef.current = null;
    };
  }, [gameIsLoading]);

  useEffect(() => {
    let indexLastHandledCommand: number = -1;
    let isStarted = false;

    BackendService.fetchCurrentCommandsForClient(sessionId, treatmentId).then((result: string[]) => {
      if (!result) {
        indexLastHandledCommand = -1;
      } else {
        indexLastHandledCommand = result.length - 1;
      }
    });

    const interval = setInterval(() => {
      const client = phaserRef?.current?.scene.keys.therapist as TaskScene;

      BackendService.fetchCurrentCommandsForClient(sessionId, treatmentId)
        .then((result: string[]) => {
          if (!result) {
            return null;
          }
          client.calledBy = 'client';
          client.isTablet = isTablet;
          while (indexLastHandledCommand + 1 < result.length) {
            indexLastHandledCommand++;
            let command = result[indexLastHandledCommand];
            let lastParsed = command.split(',');
            let isStartedArraySUIC: number[] = [];

            if (command.startsWith('SUIC:') && !hasDevice) {
              lastParsed.map(el => {
                if (el.includes('SUIC:')) {
                  let separateString = [...el];
                  let value = separateString.length - 1;
                  let result = separateString[value];
                  isStartedArraySUIC.push(Number(result));
                  let latestIsStarted = isStartedArraySUIC[0] === 0;
                  isTaskActiveParse(lastParsed);
                  if (latestIsStarted !== isStarted) {
                    setIsStarted(latestIsStarted);
                    try {
                      // it’s a small marker string the client sends back to confirm an action happened locally (an “ack”). In ClientPage it’s set to task://start or task://stop when the task begins/ends
                      const ackUri = latestIsStarted ? 'task://start' : 'task://stop';
                      BackendService.sendMediaStatusReport(sessionId, treatmentId, ackUri, 1, 1.0);
                    } catch {}
                  }

                  isStarted = latestIsStarted;
                }
              });

              client.updateTaskBasedOnEncodedCommand(command);

              if (isStarted !== client.composite.isActive) {
                if (isStarted) {
                  client.startTask();
                } else if (!isStarted) {
                  client.stopTask();
                }
              }
            } else if (command.startsWith('MCT:') && !hasDevice) {
              client.updateTaskBasedOnEncodedCommand(command);
            } else if (command.startsWith('TC:') && !hasDevice) {
              let result = command.match(/\d+/);
              let theme = result ? result[0] : null;
              setAidTheme(Number(theme));

              client.updateTaskBasedOnEncodedCommand(command);
            } else if (command.startsWith('SMC')) {
              let result = command.match(/\d+/);
              let showMessageType = result ? Number(result[0]) : null;

              setShowMessageAidScore(Number(showMessageType));

              if (showMessageType !== ShowMessageCommandTypes.Hide) {
                setOpenAidMessage(true);
              } else {
                setOpenAidMessage(false);
              }
            }
          }
        })
        .catch(e => {
          console.error('phaser expected, but ' + e);
        });
    }, 500);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTablet]);

  useEffect(() => {
    if (hasDevice) return;
    let canvas = document.querySelector('canvas');
    if (!canvas) return;

    let container = canvas.parentElement;
    if (!container) return;

    if (isConnectedToCall && !isTaskStarted) {
      container.style.visibility = 'hidden';
    } else {
      container.style.visibility = '';
    }
  }, [isConnectedToCall, isTaskStarted]);

  useEffect(() => {
    if (isTaskStarted) {
      setIsVideoConnetctionOn(false);
      return;
    }

    setIsVideoConnetctionOn(isEnabled);
  }, [isTaskStarted, isConnectedToCall, isEnabled]);
  // Everytime we connect, or disconnect, we need to pop the video to the front, or hide it.
  useEffect(() => {
    setVideoViewState(isConnectedToCall);
  }, [isConnectedToCall]);

  useEffect(() => {
    setOpenAidMessage(false);
    if (isTaskStarted || !!clientMediaURI) {
      setClientInstructionsOpened(false);
    }
  }, [isTaskStarted, clientMediaURI]);

  const isTaskActiveParse = (command: []) => {
    let reactionVisual = !!Number(command[6]);
    let reactionAudio = !!Number(command[7]);
    let calculationDifficulty = Number(command[9]) !== 0;
    let stroop = !!Number(command[15]);
    let animation = !!Number(command[14]);

    setVideoActive(reactionVisual);
    setAudioActive(reactionAudio);
    setCalculationActive(calculationDifficulty);
    setStroopActive(stroop);
    setShapeActive(animation);
  };

  return (
    <Container style={{ height, width }}>
      <ClientHeader
        clientInstructionsOpened={clientInstructionsOpened}
        setClientInstructionsOpened={setClientInstructionsOpened}
      />
      <MediaProvider>
        <Main className={aidTheme === Theme.Child ? 'child-theme' : ''}>
          <ModalUI open={openAidMessage} isCloseBtn={false} onCloseHandler={() => setOpenAidMessage(false)}>
            {aidTheme === Theme.Default ? (
              <ModalAidMessageContentAdult messageType={showMessageAidScore} />
            ) : (
              <ModalAidMessageContentChild messageType={showMessageAidScore} />
            )}
          </ModalUI>
          {!hasDevice ? <ClientMediaPlayer /> : null}
          {/* TODO: Reintroduce loader? Probably cleanup and remove {!hasDevice ? <Loader isLoading={isLoading} progress={progress} /> : null} */}
          {hasDevice ? (
            <div>
              <div
                className={'psylaris-logo'}
                style={{
                  backgroundImage: "url('/assets/psylaris-logo-bg.png'",
                  visibility: isConnectedToCall ? 'visible' : 'hidden',
                }}
              />
            </div>
          ) : null}

          <ToggleVideoView
            gameIsLoading={gameIsLoading}
            isEnabled={isVideoConnetctionOn}
            isConnected={isConnectedToCall}
          />
          {isTablet && (
            <PhaserActionButtonGroup
              isVisual={isVisualActive}
              isAudio={isAudioActive}
              isCalculation={isCalculationActive}
              isStroop={isStroopActive}
              isShape={isShapeActive}
              phaserRef={phaserRef}
            />
          )}
        </Main>
      </MediaProvider>
      <ReconnectingNotification />
    </Container>
  );
}
